import type { MutateOptions } from '@iheartjane/data-access';
import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import type { ActionResult } from '@jane/bloom/types/remixAction';
import { buildFormData } from '@jane/bloom/utils/formData';

type FormDataInput = Parameters<typeof buildFormData>[0];

// default for callers whose variables are already FormData-shaped — sent as-is.
// module-level so it doesn't destabilize the memoized `mutate`.
const identityFormData = (variables: unknown) => variables as FormDataInput;

interface UseFetcherWithMutationProps<TVariables> {
  /** Absolute route path the action lives at, e.g. `/api/aeropay-user`. */
  action: string;
  /** HTTP method — defaults to 'post'. */
  method?: 'post' | 'put' | 'patch' | 'delete';
  /** Transform component-call variables into a FormData-able shape. */
  toFormData?: (variables: TVariables) => FormDataInput;
}

/**
 * Adapts React Router's `useFetcher` to the `Mutate<TVariables, TData>` contract
 * from `@iheartjane/data-access`: one call dispatches the action and fires per-call
 * `onSuccess` / `onError` callbacks.
 *
 * Why the refs: `fetcher.submit()` is async — the response lands in `fetcher.data`
 * on a later render, so callbacks can't fire at submit time. `pendingRef` stashes
 * the per-call callbacks until the effect sees fresh data. `consumedRef` captures
 * the `fetcher.data` reference at submit time so the effect can tell a new response
 * apart from a re-run against a reference it has already handled.
 *
 * Concurrent submits: `pendingRef` is last-write-wins, matching `useFetcher`'s own
 * behavior of canceling the in-flight request when a new submit arrives.
 */
export function useFetcherWithMutation<TVariables, TData>({
  action,
  method = 'post',
  toFormData = identityFormData,
}: UseFetcherWithMutationProps<TVariables>) {
  const fetcher = useFetcher<ActionResult<TData>>();
  const pendingRef = useRef<MutateOptions<TData> | null>(null);
  // tracks the last fetcher.data reference we've already fired callbacks for;
  // prevents a stale wakeup from refiring against a fresh pendingRef
  const consumedRef = useRef<ActionResult<TData> | undefined>(undefined);

  useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data || !pendingRef.current)
      return;

    // stale wakeup — same data object we already consumed
    if (fetcher.data === consumedRef.current) return;

    consumedRef.current = fetcher.data;
    const opts = pendingRef.current;
    pendingRef.current = null;
    if (fetcher.data.success) {
      opts.onSuccess?.(fetcher.data.payload);
    } else {
      opts.onError?.(fetcher.data.errors);
    }
  }, [fetcher.state, fetcher.data]);

  // drop pending on unmount to avoid late-fire on a stale closure
  useEffect(
    () => () => {
      pendingRef.current = null;
    },
    []
  );

  const mutate = useCallback(
    (variables: TVariables, options?: MutateOptions<TData>) => {
      pendingRef.current = options ?? null;
      // capture the pre-submit data reference so only a strictly newer
      // response triggers callbacks
      consumedRef.current = fetcher.data;
      fetcher.submit(buildFormData(toFormData(variables)), { action, method });
    },
    [action, method, toFormData, fetcher]
  );

  // isPending (React Query v5) and isLoading (v4) are aliases — both reflect
  // the in-flight state so consumers can use either convention
  const isPending = fetcher.state !== 'idle';
  return { isLoading: isPending, isPending, mutate };
}
